import { Link } from 'react-router-dom';
import { useDashboard } from '../lib/hooks.js';
import { Badge, Card, EmptyState, Skeleton, StatCard } from '../ui/index.js';
import { label, money, statusTone } from '../lib/format.js';

export function Dashboard() {
  const { data, isLoading, error } = useDashboard();

  if (isLoading) return <LoadingDashboard />;
  if (error) return <Card className="card-pad"><EmptyState icon="⚠️" title="Couldn't load the dashboard">The API may not be running. Start it with <code>npm run dev -w packages/api</code>.</EmptyState></Card>;

  const scheduling = data?.jobsNeedingScheduling ?? [];
  const estimates = data?.estimatesAwaitingResponse ?? [];
  const overdue = data?.overdueInvoices ?? [];
  const overdueTotal = overdue.reduce((s, i) => s + (i.balanceCents ?? 0), 0);
  const estimatesTotal = estimates.reduce((s, e) => s + (e.totalCents ?? 0), 0);

  return (
    <>
      <div className="page-head">
        <div>
          <div className="page-title">Good to see you 👋</div>
          <div className="page-sub">Here's what needs your attention today.</div>
        </div>
      </div>

      <div className="grid grid-4" style={{ marginBottom: 'var(--sp-6)' }}>
        <StatCard label="Jobs to schedule" value={scheduling.length} tone={scheduling.length ? 'warning' : 'neutral'} sub="unassigned work" />
        <StatCard label="Estimates open" value={estimates.length} tone="info" sub={money(estimatesTotal) + ' in play'} />
        <StatCard label="Overdue invoices" value={overdue.length} tone={overdue.length ? 'danger' : 'success'} sub={money(overdueTotal) + ' outstanding'} />
        <StatCard label="Needs follow-up" value={scheduling.length + estimates.length + overdue.length} sub="total action items" />
      </div>

      <div className="grid grid-3">
        <ActionQueue title="Jobs needing scheduling" empty="All jobs are scheduled 🎉">
          {scheduling.map((j) => (
            <Link key={j.id} to={`/customers/${j.customerId}`} className="queue-item" style={{ textDecoration: 'none', color: 'inherit' }}>
              <div className="stack"><strong style={{ fontSize: 'var(--text-sm)' }}>{j.title}</strong><span className="subtle" style={{ fontSize: 'var(--text-xs)' }}>{j.number}</span></div>
              <Badge tone={statusTone(j.priority)}>{label(j.priority)}</Badge>
            </Link>
          ))}
        </ActionQueue>

        <ActionQueue title="Estimates awaiting response" empty="No estimates are waiting.">
          {estimates.map((e) => (
            <Link key={e.id} to={`/customers/${e.customerId}`} className="queue-item" style={{ textDecoration: 'none', color: 'inherit' }}>
              <div className="stack"><strong style={{ fontSize: 'var(--text-sm)' }}>{e.title ?? e.number}</strong><span className="subtle" style={{ fontSize: 'var(--text-xs)' }}>{money(e.totalCents)}</span></div>
              <Badge tone={statusTone(e.status)}>{label(e.status)}</Badge>
            </Link>
          ))}
        </ActionQueue>

        <ActionQueue title="Overdue invoices" empty="Nothing overdue — nice.">
          {overdue.map((i) => (
            <Link key={i.id} to={`/customers/${i.customerId}`} className="queue-item" style={{ textDecoration: 'none', color: 'inherit' }}>
              <div className="stack"><strong style={{ fontSize: 'var(--text-sm)' }}>{i.number}</strong><span className="subtle" style={{ fontSize: 'var(--text-xs)' }}>due {i.dueDate}</span></div>
              <strong className="tabular" style={{ color: 'var(--color-danger)' }}>{money(i.balanceCents)}</strong>
            </Link>
          ))}
        </ActionQueue>
      </div>
    </>
  );
}

function ActionQueue({ title, empty, children }: { title: string; empty: string; children: React.ReactNode }) {
  const items = Array.isArray(children) ? children : [children];
  const isEmpty = items.filter(Boolean).length === 0;
  return (
    <Card>
      <div className="card-head"><span className="card-title">{title}</span></div>
      <div style={{ padding: '0 var(--sp-5)' }}>
        {isEmpty ? <div style={{ padding: 'var(--sp-6) 0' }}><span className="muted" style={{ fontSize: 'var(--text-sm)' }}>{empty}</span></div> : children}
      </div>
    </Card>
  );
}

function LoadingDashboard() {
  return (
    <>
      <div className="page-head"><Skeleton h={28} w={220} /></div>
      <div className="grid grid-4" style={{ marginBottom: 'var(--sp-6)' }}>
        {[0, 1, 2, 3].map((i) => <Card key={i} className="card-pad"><Skeleton h={48} /></Card>)}
      </div>
      <div className="grid grid-3">{[0, 1, 2].map((i) => <Card key={i} className="card-pad"><Skeleton h={140} /></Card>)}</div>
    </>
  );
}
